import { Colors } from "@/constants/theme";
import { useAppendChatMessage } from "@/hooks/queries/use-messages-query";
import { useColorScheme } from "@/hooks/use-color-scheme";
import { trackLogin, trackSignOut, trackSignUp } from "@/lib/analytics-events";
import authService from "@/lib/auth";
import { resetEcho } from "@/lib/echo";
import { locationTrackingService } from "@/lib/services/location-tracking";
import { webSocketService } from "@/lib/services/websocket";
import { showToast } from "@/lib/toast";
import { User } from "@/lib/types/user";
import { useAuthStore } from "@/stores/use-auth-store";
import { useLocationStore } from "@/stores/use-location-store";
import { useQueryClient } from "@tanstack/react-query";
import { router } from "expo-router";
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { AppState, AppStateStatus } from "react-native";

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  signUp: (data: any) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUser: (user: User) => void;
  theme: any;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function isSocketActiveState(state: AppStateStatus): boolean {
  return state === "active" || state === "background" || state === "inactive";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const colorScheme = useColorScheme();
  const qc = useQueryClient();
  const theme = Colors[colorScheme ?? "light"];
  const [appState, setAppState] = useState<boolean>(() => {
    return isSocketActiveState(AppState.currentState);
  });
  const { user, token, logIn, logOut, setUser, _hasHydrated, activeBooking } =
    useAuthStore((state) => state);
  const appendMessage = useAppendChatMessage(activeBooking);
  const setCoordinate = useLocationStore((state) => state.setCoordinate);
  const isDriverOnline =
    user?.role === "driver" && (user?.driver?.is_online ?? false);

  useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      setAppState(isSocketActiveState(nextAppState));
    };

    const subscription = AppState.addEventListener(
      "change",
      handleAppStateChange,
    );

    return () => {
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    if (activeBooking && appState) {
      webSocketService.connect(activeBooking);
    } else {
      webSocketService.disconnect();
    }
  }, [activeBooking, appState]);

  useEffect(() => {
    const unsubscribe = webSocketService.subscribe((payload) => {
      if (payload) {
        appendMessage(payload!);
      }
    });

    return () => unsubscribe();
  }, [appendMessage]);

  useEffect(() => {
    if (!isDriverOnline) {
      return;
    }

    let cancelled = false;

    const startDriverTracking = async () => {
      const currentLocation =
        await locationTrackingService.getCurrentLocation();
      if (!cancelled && currentLocation) {
        setCoordinate(currentLocation);
      }

      if (!cancelled) {
        await locationTrackingService.startTracking((location) => {
          setCoordinate(location);
        }, activeBooking ?? undefined);
      }
    };

    startDriverTracking().catch((error) => {
      console.error("Failed to start driver location tracking:", error);
    });

    return () => {
      cancelled = true;
    };
  }, [isDriverOnline, activeBooking, setCoordinate]);

  useEffect(() => {
    if (user?.role !== "driver" || isDriverOnline) {
      return;
    }

    locationTrackingService.stopTracking();
    locationTrackingService.disconnectDispatch();
  }, [user?.role, isDriverOnline]);

  const signIn = async (email: string, password: string) => {
    const response = await authService.login({
      email,
      password,
    });
    logIn({ user: response.user, token: response.token });
    trackLogin("email", response.user.role);
    return response.user;
  };

  const signUp = async (data: any) => {
    await authService.register(data);
    trackSignUp("email", data.role);

    try {
      const response = await authService.login({
        email: data.email,
        password: data.password,
      });
      logIn({ user: response.user, token: response.token });
    } catch {
      router.replace("/(auth)");
    }
  };

  const signOut = async () => {
    trackSignOut();
    webSocketService.disconnect();
    await locationTrackingService.stopTracking();
    locationTrackingService.disconnectDispatch();
    resetEcho();
    await qc.cancelQueries();
    qc.invalidateQueries({ refetchType: "none" });
    await authService.logout();
    logOut();
    router.replace("/(auth)");
  };

  const refreshUser = async () => {
    try {
      const userData = await authService.getUser();
      setUser(userData);
    } catch (error) {
      showToast.error("Error refreshing user");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isLoading: !_hasHydrated,
        user,
        token,
        signIn,
        signUp,
        signOut,
        refreshUser,
        setUser,
        theme,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
